import axios from 'axios'
import moment from 'moment'
import { PLANT } from '@/constants/stock-count'
import { hasCommonElement } from '@/utils/helper_functions'
import { SUPER_MANAGER, SUPER_MANAGER_READ_ONLY } from '@/constants/permissions'

const BASE_URL = process.env.VUE_APP_STOCK_API_ENDPOINT
const detailEndpoint = '/user_api/v1/dashboard/detailed_view'
const detailUrl = (
  process.env.VUE_APP_USER_API_ENDPOINT +
  detailEndpoint
)
const compactEndpoint = '/user_api/v1/dashboard/compact_view'
const compactUrl = (
  process.env.VUE_APP_USER_API_ENDPOINT +
  compactEndpoint
)
const frontLinerDashboardEndpoint = '/api/v2/my_stock_count/occupancy_report'
const frontLineDashboardUrl = (
  BASE_URL +
  frontLinerDashboardEndpoint
)
export async function getPlantsApi () {
  const url = BASE_URL + `/api/v2/relationship/${PLANT}`
  try {
    const response = await axios.get(url)
    return response
  } catch (error) {
    console.error({ _: 'Get Plants failed', url, error })
    throw error
  }
}

function parseDetailItems (item) {
  return {
    file_type: item.file_type,
    start_date: item.start_date ? moment(item.start_date).format('lll') : 'N/A',
    end_date: item.end_date ? moment(item.end_date).format('lll') : 'N/A',
    file_name: item.file_name ? item.file_name : 'N/A',
    file_part_of_day: item.file_part_of_day,
    status: item.status
  }
}

function parseCompactItems (item) {
  return {
    date: item.date,
    number_of_files: item.number_of_files,
    number_of_processed_files: item.number_of_processed_files,
    number_of_errored_files: item.number_of_errored_files,
    number_of_missing_files: item.number_of_missing_files,
    number_of_in_progress_files: item.number_of_in_progress_files,
    number_of_not_processed_files: item.number_of_not_processed_files
  }
}
function parseFrontlinerItems (item) {
  const items = []
  if (!item) {
    return items
  }
  const totalStats = item.total_stats ? item.total_stats : null
  const frontlineStats = item.frontline_stats
  items.push(...frontlineStats)
  if (totalStats) {
    items.push(totalStats)
  }
  return items.map(item => ({
    conducted_by: item.conducted_by,
    total_tasks: item.total_tasks,
    completed_count: item.completed_count,
    not_completed_count: item.not_completed_count,
    completed_percentage: item.completed_percentage ?? 0,
    not_completed_percentage: item.not_completed_percentage ?? 0
  }))
}

function getFileDashboardDetailData (parameters) {
  const { date } = parameters
  const apiUrl = detailUrl + `?date=${date}`
  const requestPromise = axios.get(apiUrl)
    .catch(error => {
      console.error({ _: 'getFileDashboardDetailData failed', parameters, detailUrl, error })
      throw error
    })
  return requestPromise
}
function getFileDashboardCompactData (parameters) {
  const { startDate, endDate } = parameters
  const apiUrl = compactUrl + `?start_date=${moment(startDate).format('YYYY-MM-DD')}&end_date=${moment(endDate).format('YYYY-MM-DD')}`
  const requestPromise = axios.get(apiUrl)
    .catch(error => {
      console.error({ _: 'getFileDashboardCompactData failed', parameters, detailUrl, error })
      throw error
    })
  return requestPromise
}
function getFrontlinerDashboardData (parameters) {
  const { startDate, endDate, plant, plannerGroup, email } = parameters
  let apiUrl = frontLineDashboardUrl + `?start_date=${moment(startDate).format('YYYY-MM-DD')}&end_date=${moment(endDate).format('YYYY-MM-DD')}`
  if (plant) {
    apiUrl += `&plant=${plant}`
  } else if (plannerGroup) {
    apiUrl += `&planner_group=${plannerGroup}`
  }
  if (email) {
    apiUrl += `&conducted_by=${email}`
  }
  const requestPromise = axios.get(apiUrl)
    .catch(error => {
      console.error({ _: 'getFileDashboardCompactData failed', parameters, detailUrl, error })
      throw error
    })
  return requestPromise
}
export default {
  namespaced: true,
  state: {
    dashboardData: [],
    dashboardListData: [],
    frontLinerDashboardData: [],
    plants: [],
    ongoingQuery: false
  },
  getters: {
    getPlannerGroups: (state, getters, rootState) => {
      return rootState.profile.plannerGroups
    },
    getPlants: (state) => {
      return state.plants
    },
    dashboardData (state) {
      return state.dashboardData
    },
    dashboardListData (state) {
      return state.dashboardListData
    },
    frontLinerDashboardData (state) {
      return state.frontLinerDashboardData
    },
    ongoingQuery (state) {
      return state.ongoingQuery
    }
  },
  mutations: {
    setPlants (state, plants) {
      state.plants = plants.filter(plant => plant !== '')
    },
    setDashboardData (state, data) {
      state.dashboardData = data
    },
    setDashboardListData (state, data) {
      state.dashboardListData = data
    },
    setFrontLinerDashboardData (state, data) {
      state.frontLinerDashboardData = data
    },
    setOngoingQuery (state, value) {
      state.ongoingQuery = value
    }
  },
  actions: {
    async fetchPlants ({ commit, rootState }) {
      const userPermissions = rootState.profile.permissions
      if (hasCommonElement(userPermissions, [SUPER_MANAGER, SUPER_MANAGER_READ_ONLY])) {
        getPlantsApi()
          .then((response) => {
            commit('setPlants', response.data.plants)
            return response
          })
          .catch((error) => {
            console.error({ _: 'Get Plants failed', error })
          })
      } else {
        commit('setPlants', rootState.profile.plants)
      }
    },

    async getFileDashboardDetailData ({ commit }, parameters) {
      commit('setOngoingQuery', true)
      const requestPromise = getFileDashboardDetailData(parameters)
        .then(response => {
          const data = response.data
          const logs = data.map(parseDetailItems)
          commit('setDashboardData', logs)
        })
        .catch(error => {
          console.error({ _: 'getFileDashboardDetailData failed', parameters, error })
        })
        .finally(() => {
          commit('setOngoingQuery', false)
        }
        )
      return requestPromise
    },
    async getFileDashboardCompactData ({ commit }, parameters) {
      commit('setOngoingQuery', true)
      const requestPromise = getFileDashboardCompactData(parameters)
        .then(response => {
          const data = response.data
          const logs = data.map(parseCompactItems)
          commit('setDashboardListData', logs)
        })
        .catch(error => {
          console.error({ _: 'getFileDashboardCompactData failed', parameters, error })
        })
        .finally(() => {
          commit('setOngoingQuery', false)
        }
        )
      return requestPromise
    },
    async getFrontlinerDashboardData ({ commit }, parameters) {
      commit('setOngoingQuery', true)
      const requestPromise = getFrontlinerDashboardData(parameters)
        .then(response => {
          const data = response.data
          const logs = parseFrontlinerItems(data)
          commit('setFrontLinerDashboardData', logs)
        })
        .catch(error => {
          commit('setFrontLinerDashboardData', [])
          console.error({ _: 'getFrontlinerDashboardData failed', parameters, error })
          commit('setErrorMessage', error?.response?.data ?? error, { root: true })
        })
        .finally(() => {
          commit('setOngoingQuery', false)
        })
      return requestPromise
    }

  }
}
